import { useEffect, useState, useMemo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { DataTableAdvanced, type DataTableColumn, type DataTableFilter } from '@/components/ui/data-table-advanced'
import { Badge } from '@/components/ui/badge'
import { apiService } from '@/services/api'
import { Plus, MoreHorizontal, Edit, Eye, Trash2, Calendar, Copy, TrendingUp, Flame, Zap, Loader2, Save, Send } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { format } from 'date-fns'
import { useDrawer } from '@/components/ui/drawer/index'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { toast } from 'sonner'
import { BlogPostFormDrawer } from '@/components/blog/BlogPostFormDrawer'
import { BlogPostPreview } from '@/components/blog/BlogPostPreview'
import { useConfirm } from '@/hooks/useConfirm'
import { BulkOperationsToolbar } from '@/components/blog/BulkOperationsToolbar'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { StatusBadge } from '@/components/blog/StatusBadge'
import { AIWriterDrawer } from '@/components/blog/AIWriterDrawer'
import { AIGenerationManager } from '@/components/blog/AIGenerationManager'
import { ReleasePostDrawer } from '@/components/blog/ReleasePostDrawer'
import { HeadlineReviewDialog } from '@/components/blog/HeadlineReviewDialog'
import { OutlineReviewDialog } from '@/components/blog/OutlineReviewDialog'
import { LengthWarningDialog } from '@/components/blog/LengthWarningDialog'
import { BlogCreationModeDrawer } from '@/components/blog/BlogCreationModeDrawer'

// Blog post type based on our API response
interface BlogPost {
  id: string
  slug: string
  title: string
  content: string
  excerpt?: string
  status: 'draft' | 'review' | 'published' | 'scheduled'
  author_id?: string
  published_at?: string
  scheduled_at?: string
  seo_title?: string
  seo_description?: string
  featured_image?: string
  reading_time_minutes?: number
  view_count: number
  created_at: string
  updated_at: string
  categories: Array<{
    id: string
    name: string
    slug: string
    color?: string
  }>
  tags: Array<{
    id: string
    name: string
    slug: string
  }>
  ai_metadata?: {
    phase: 'researching' | 'generating_headlines' | 'awaiting_headline_approval' | 'outlining' | 'awaiting_outline_approval' | 'writing' | 'awaiting_length_decision' | 'optimizing' | 'ready' | 'failed'
    cost?: number
    tokens_used?: number
    error?: string
    headline_options?: string[]
    outline?: string
    word_count?: number
    length_warning?: boolean
    seo_warnings?: string[]
  }
}

export const BlogPosts: React.FC = () => {
  const { openDrawer, closeDrawer, updateDrawer } = useDrawer()
  const { onMessage } = useDrawerMessaging('blog-posts-page')
  const { confirm, ConfirmDialog } = useConfirm()
  const { hasPermission } = usePermissions()
  const { user } = useAuth()
  const [selectedPosts, setSelectedPosts] = useState<BlogPost[]>([])
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([])
  const [tags, setTags] = useState<Array<{ id: string; name: string }>>([])

  // AI Writer dialogs state
  const [headlineReviewPost, setHeadlineReviewPost] = useState<BlogPost | null>(null)
  const [outlineReviewPost, setOutlineReviewPost] = useState<BlogPost | null>(null)
  const [lengthWarningPost, setLengthWarningPost] = useState<BlogPost | null>(null)

  // React Query for posts with conditional polling
  const { data: posts = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const data = await apiService.blog.posts.list()
      return data as BlogPost[]
    },
    refetchInterval: (query) => {
      // Poll every 3s if any post has active AI generation phase
      const hasActivePosts = query.state.data?.some((post: BlogPost) =>
        post.ai_metadata?.phase &&
        !['ready', 'failed'].includes(post.ai_metadata.phase)
      )
      return hasActivePosts ? 3000 : false
    },
  })

  useEffect(() => {
    fetchCategories()
    fetchTags()
  }, [])

  // Listen for refresh messages from drawers
  useEffect(() => {
    return onMessage((message) => {
      if (message.action === 'refresh') {
        refetch()
      }
    })
  }, [onMessage, refetch])

  // Auto-open approval dialogs - DISABLED
  // This is now handled by AIGenerationManager within the drawer for consistency
  // useEffect(() => {
  //   if (!posts || posts.length === 0) return

  //   // Priority 1: Check for headline approval first
  //   const awaitingHeadline = posts.find(
  //     (p) => p.ai_metadata?.phase === 'awaiting_headline_approval'
  //   )
  //   if (awaitingHeadline) {
  //     setHeadlineReviewPost(awaitingHeadline)
  //     return
  //   }

  //   // Priority 2: Check for outline approval
  //   const awaitingOutline = posts.find(
  //     (p) => p.ai_metadata?.phase === 'awaiting_outline_approval'
  //   )
  //   if (awaitingOutline) {
  //     setOutlineReviewPost(awaitingOutline)
  //     return
  //   }

  //   // Priority 3: Check for length decision
  //   const awaitingLengthDecision = posts.find(
  //     (p) => p.ai_metadata?.phase === 'awaiting_length_decision'
  //   )
  //   if (awaitingLengthDecision) {
  //     setLengthWarningPost(awaitingLengthDecision)
  //   }
  // }, [posts])

  const fetchCategories = async () => {
    try {
      const data = await apiService.blog.categories.list()
      setCategories(data)
    } catch (err) {
      console.error('Failed to fetch categories:', err)
    }
  }

  const fetchTags = async () => {
    try {
      const data = await apiService.blog.tags.list()
      setTags(data)
    } catch (err) {
      console.error('Failed to fetch tags:', err)
    }
  }

  const handleBulkOperationComplete = useCallback(() => {
    setSelectedPosts([])
    refetch()
  }, [refetch])

  const handleRowSelect = useCallback((rows: BlogPost[]) => {
    setSelectedPosts(rows)
  }, [])

  // Helper function to create footer for blog post drawers
  const createBlogPostFooter = (
    drawerId: string,
    state: {
      loading: boolean
      isSaving: boolean
      lastSaved: Date | null
      hasUnsavedChanges: boolean
      handleSubmit: (action: 'draft' | 'review' | 'publish' | 'schedule') => Promise<void>
      status: 'draft' | 'review' | 'published' | 'scheduled'
    },
    post?: BlogPost
  ) => {
    // Check if user can publish posts
    const canPublish = hasPermission('publish_blog_posts')

    return (
      <div className="p-4 flex items-center justify-between">
        <div className="text-sm flex items-center gap-2">
          {state.isSaving && (
            <span className="flex items-center gap-2 text-blue-600 font-medium">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          )}
          {!state.isSaving && state.lastSaved && (
            <span className="flex items-center gap-2 text-green-600">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              All changes saved at {format(state.lastSaved, 'HH:mm:ss')}
            </span>
          )}
          {!state.isSaving && !state.lastSaved && state.hasUnsavedChanges && (
            <span className="text-amber-600 font-medium">Unsaved changes</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => closeDrawer(drawerId)}
            disabled={state.loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => state.handleSubmit('draft')}
            disabled={state.loading}
          >
            {state.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            Save as Draft
          </Button>
          {canPublish && state.status !== 'published' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => state.handleSubmit('review')}
              disabled={state.loading}
            >
              {state.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="mr-2 h-4 w-4" />
              Submit for Review
            </Button>
          )}
          {canPublish && (
            <Button
              type="button"
              onClick={() => state.handleSubmit('publish')}
              disabled={state.loading}
            >
              {state.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {post ? 'Update Post' : 'Publish Now'}
            </Button>
          )}
          {!canPublish && (
            <p className="text-sm text-muted-foreground">
              Draft only - publishing requires approval
            </p>
          )}
        </div>
      </div>
    )
  }

  const handleCreatePost = () => {
    const drawerId = 'blog-creation-mode'
    openDrawer({
      id: drawerId,
      title: 'Create a New Article',
      content: <BlogCreationModeDrawer
        onSelectManual={() => {
          closeDrawer(drawerId)
          handleManualCreation()
        }}
        onSelectAI={() => {
          closeDrawer(drawerId)
          handleOpenAIWriterDrawer()
        }}
        onSelectReleasePost={() => {
          closeDrawer(drawerId)
          handleOpenReleaseDrawer()
        }}
      />,
      size: 'md',
    })
  }

  const handleManualCreation = () => {
    const drawerId = 'create-blog-post'
    openDrawer({
      id: drawerId,
      title: 'Create Article',
      content: <BlogPostFormDrawer
        onClose={() => closeDrawer(drawerId)}
        onFormStateChange={(state) => {
          updateDrawer(drawerId, {
            footer: createBlogPostFooter(drawerId, state)
          })
        }}
      />,
      size: '80',
      footer: <div className="p-4" />, // Initial empty footer
    })
  }

  const handleOpenAIWriterDrawer = () => {
    const drawerId = 'ai-writer'
    openDrawer({
      id: drawerId,
      title: 'AI Article Writer - Answer 10 Questions',
      content: <AIWriterDrawer
        drawerId={drawerId}
        onComplete={async (postId) => {
          // Close AI writer drawer
          closeDrawer(drawerId)

          // Refetch posts to update the list
          await refetch()

          // Fetch the completed post and open edit drawer
          try {
            const completedPost = await apiService.blog.posts.get(postId)
            handleEditPost(completedPost as BlogPost)
          } catch (error) {
            console.error('Failed to open post for editing:', error)
            toast.error('Article created successfully, but failed to open editor')
          }
        }}
        onStateChange={(state) => {
          // Create footer dynamically based on state
          const footer = (
            <div className="flex justify-between gap-2 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={state.handleBack}
                disabled={state.currentQuestionIndex === 0 || state.isLoading}
                className="border-border hover:bg-accent"
              >
                Back
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => closeDrawer(drawerId)}
                  className="border-border hover:bg-accent"
                >
                  Cancel
                </Button>

                {!state.isLastQuestion && state.hasCurrentQuestion && (
                  <Button
                    onClick={state.handleNext}
                    disabled={!state.currentAnswer?.trim() || state.isLoading}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Next
                  </Button>
                )}

                {(state.isLastQuestion || !state.hasCurrentQuestion) && (
                  <Button
                    onClick={state.handleGenerate}
                    disabled={(!state.currentAnswer?.trim() && state.hasCurrentQuestion) || state.isGenerating}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {state.isGenerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      'Start Generating'
                    )}
                  </Button>
                )}
              </div>
            </div>
          )
          updateDrawer(drawerId, { footer })
        }}
      />,
      size: 'lg',
      footer: <div className="p-4" />, // Initial empty footer
    })
  }

  const handleOpenReleaseDrawer = () => {
    const drawerId = 'release-post'
    openDrawer({
      id: drawerId,
      title: 'Generate Release Post from Loom Video',
      content: <ReleasePostDrawer
        drawerId={drawerId}
        onComplete={async (postId) => {
          // Close release post drawer
          closeDrawer(drawerId)

          // Refetch posts to update the list
          await refetch()

          // Fetch the completed post and open edit drawer
          try {
            const completedPost = await apiService.blog.posts.get(postId)
            handleEditPost(completedPost as BlogPost)
          } catch (error) {
            console.error('Failed to open post for editing:', error)
            toast.error('Release post created successfully, but failed to open editor')
          }
        }}
        onStateChange={(state) => {
          // Create footer dynamically based on state
          const footer = (
            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => closeDrawer(drawerId)}
                className="border-border hover:bg-accent"
              >
                Cancel
              </Button>
              <Button
                onClick={state.handleSubmit}
                disabled={!state.isValid || state.isSubmitting || state.isExtracting}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {state.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate Release Post'
                )}
              </Button>
            </div>
          )
          updateDrawer(drawerId, { footer })
        }}
      />,
      size: 'lg',
      footer: <div className="p-4" />, // Initial empty footer
    })
  }

  const handleEditPost = (post: BlogPost) => {
    const drawerId = `edit-blog-post-${post.id}`
    openDrawer({
      id: drawerId,
      title: `Edit: ${post.title}`,
      content: <BlogPostFormDrawer
        post={post}
        onClose={() => closeDrawer(drawerId)}
        onFormStateChange={(state) => {
          updateDrawer(drawerId, {
            footer: createBlogPostFooter(drawerId, state, post)
          })
        }}
      />,
      size: '80',
      footer: <div className="p-4" />, // Initial empty footer
    })
  }

  const handleViewPost = (post: BlogPost) => {
    const drawerId = `view-blog-post-${post.id}`
    openDrawer({
      id: drawerId,
      title: post.title,
      content: (
        <BlogPostPreview
          title={post.title}
          content={post.content}
          excerpt={post.excerpt}
          featuredImage={post.featured_image}
          categories={post.categories}
          tags={post.tags}
          publishedAt={post.published_at ? new Date(post.published_at) : new Date()}
          readingTime={post.reading_time_minutes || 5}
        />
      ),
      size: '80',
    })
  }

  const handleDuplicatePost = async (post: BlogPost) => {
    const duplicatedPost = {
      ...post,
      title: `${post.title} (Copy)`,
      slug: `${post.slug}-copy`,
      status: 'draft' as const,
      published_at: undefined,
      scheduled_at: undefined,
    }
    const drawerId = `duplicate-blog-post-${post.id}`
    openDrawer({
      id: drawerId,
      title: 'Duplicate Post',
      content: <BlogPostFormDrawer
        post={duplicatedPost}
        onClose={() => closeDrawer(drawerId)}
        onFormStateChange={(state) => {
          updateDrawer(drawerId, {
            footer: createBlogPostFooter(drawerId, state)
          })
        }}
      />,
      size: '80',
      footer: <div className="p-4" />, // Initial empty footer
    })
  }

  const handleUpdateStatus = async (post: BlogPost, newStatus: BlogPost['status']) => {
    try {
      await apiService.blog.posts.update(post.id, { status: newStatus })
      toast.success(`Post status updated to ${newStatus}`)
      refetch()
    } catch (err) {
      console.error('Failed to update post status:', err)
      toast.error('Failed to update post status')
    }
  }

  const handleDeletePost = (post: BlogPost) => {
    confirm({
      title: 'Delete Article',
      message: `Are you sure you want to delete "${post.title}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await apiService.blog.posts.delete(post.id)
          toast.success('Article deleted successfully')
          refetch()
        } catch (err) {
          console.error('Failed to delete blog post:', err)
          toast.error('Failed to delete article')
        }
      }
    })
  }

  const handleRowClick = (post: BlogPost) => {
    // Check if post has AI generation phase that requires user interaction
    const phase = post.ai_metadata?.phase
    if (!phase) return

    // Active generation phases that user might want to monitor
    const activePhases = ['researching', 'generating_headlines', 'outlining', 'writing', 'optimizing']
    // Approval phases that require user interaction
    const approvalPhases = ['awaiting_headline_approval', 'awaiting_outline_approval', 'awaiting_length_decision']

    // Only open drawer if post is actively generating or needs approval
    if (activePhases.includes(phase) || approvalPhases.includes(phase)) {
      const drawerId = `ai-generation-${post.id}`
      openDrawer({
        id: drawerId,
        title: 'AI Article Generation',
        content: (
          <AIGenerationManager
            postId={post.id}
            drawerId={drawerId}
            onComplete={async (postId) => {
              // Close drawer when generation completes
              closeDrawer(drawerId)
              // Refetch posts to update the list
              await refetch()
              // Open the editor
              handleEditPost(posts.find(p => p.id === postId) || post)
            }}
          />
        ),
        footer: null // AIGenerationManager manages its own footer
      })
    }
  }

  const getStatusBadgeClass = (status: BlogPost['status']) => {
    switch (status) {
      case 'published':
        return 'bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
      case 'draft':
        return 'bg-muted text-muted-foreground hover:bg-muted/80'
      case 'review':
        return 'bg-yellow-600 text-white hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-600'
      case 'scheduled':
        return 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
      default:
        return 'bg-muted text-muted-foreground hover:bg-muted/80'
    }
  }

  const getPerformanceIndicator = (viewCount: number) => {
    // Calculate performance thresholds based on all posts
    const allViews = posts.map(p => p.view_count)
    const avgViews = allViews.length > 0 ? allViews.reduce((a, b) => a + b, 0) / allViews.length : 0
    const maxViews = Math.max(...allViews, 1)

    // Viral: Top 5% or > 10,000 views
    if (viewCount >= maxViews * 0.95 || viewCount > 10000) {
      return {
        icon: Flame,
        color: 'text-red-600 dark:text-red-500',
        label: 'Viral',
      }
    }
    // Hot: Above 2x average or > 5,000 views
    if (viewCount >= avgViews * 2 || viewCount > 5000) {
      return {
        icon: Zap,
        color: 'text-orange-600 dark:text-orange-500',
        label: 'Hot',
      }
    }
    // Trending: Above average or > 1,000 views
    if (viewCount >= avgViews || viewCount > 1000) {
      return {
        icon: TrendingUp,
        color: 'text-blue-600 dark:text-blue-500',
        label: 'Trending',
      }
    }
    // Normal
    return {
      icon: Eye,
      color: 'text-muted-foreground',
      label: 'Normal',
    }
  }

  const columns: DataTableColumn<BlogPost>[] = useMemo(() => [
    {
      id: 'title',
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div className="max-w-xs">
          <div className="font-medium truncate">{row.original.title}</div>
          {row.original.excerpt && (
            <div className="text-sm text-muted-foreground truncate mt-1">
              {row.original.excerpt}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <Badge className={getStatusBadgeClass(row.original.status)}>
            {row.original.status}
          </Badge>
          {row.original.ai_metadata?.phase && (
            <StatusBadge
              phase={row.original.ai_metadata.phase}
              error={row.original.ai_metadata.error}
            />
          )}
        </div>
      ),
    },
    {
      id: 'categories',
      accessorKey: 'categories',
      header: 'Categories',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.categories.slice(0, 2).map((category) => (
            <Badge
              key={category.id}
              variant="outline"
              className="text-xs"
              style={{ borderColor: category.color }}
            >
              {category.name}
            </Badge>
          ))}
          {row.original.categories.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{row.original.categories.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'view_count',
      accessorKey: 'view_count',
      header: 'Views',
      cell: ({ row }) => {
        const indicator = getPerformanceIndicator(row.original.view_count)
        const Icon = indicator.icon
        return (
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${indicator.color}`} />
            <div className="flex flex-col">
              <span className="font-medium text-foreground">
                {row.original.view_count.toLocaleString()}
              </span>
              {indicator.label !== 'Normal' && (
                <span className={`text-xs ${indicator.color}`}>
                  {indicator.label}
                </span>
              )}
            </div>
          </div>
        )
      },
    },
    {
      id: 'published_at',
      accessorKey: 'published_at',
      header: 'Published',
      cell: ({ row }) => {
        const publishedAt = row.original.published_at
        if (!publishedAt) return <span className="text-muted-foreground">-</span>
        return (
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {format(new Date(publishedAt), 'MMM d, yyyy')}
          </div>
        )
      },
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">
          {format(new Date(row.original.created_at), 'MMM d, yyyy')}
        </div>
      ),
    },
    {
      id: 'actions',
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleViewPost(row.original)}>
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleEditPost(row.original)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleDuplicatePost(row.original)}>
              <Copy className="mr-2 h-4 w-4" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Status</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => handleUpdateStatus(row.original, 'draft')}
              disabled={row.original.status === 'draft'}
            >
              Draft
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleUpdateStatus(row.original, 'review')}
              disabled={row.original.status === 'review'}
            >
              Review
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleUpdateStatus(row.original, 'published')}
              disabled={row.original.status === 'published'}
            >
              Published
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleUpdateStatus(row.original, 'scheduled')}
              disabled={row.original.status === 'scheduled'}
            >
              Scheduled
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* Only show delete if user can delete all posts OR can delete own posts and is the author */}
            {(hasPermission('delete_all_blog_posts') ||
              (hasPermission('delete_own_blog_posts') && row.original.author_id === user?.id)) && (
              <DropdownMenuItem
                onClick={() => handleDeletePost(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [posts, hasPermission, user])

  const filters: DataTableFilter[] = [
    {
      column: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Review', value: 'review' },
        { label: 'Published', value: 'published' },
        { label: 'Scheduled', value: 'scheduled' },
      ],
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Articles</h1>
          <p className="text-muted-foreground">
            Manage your article content and marketing posts
          </p>
        </div>
        <Button onClick={handleCreatePost} className="gap-2">
          <Plus className="h-4 w-4" />
          New Post
        </Button>
      </div>

      {/* Data Table */}
      <DataTableAdvanced
        data={posts}
        columns={columns}
        filters={filters}
        loading={loading}
        searchable={true}
        searchKeys={['title', 'excerpt']}
        searchPlaceholder="Search articles..."
        emptyMessage="No articles found"
        showSelection={true}
        onRowSelect={handleRowSelect}
        onRowClick={handleRowClick}
        toolbar={
          <BulkOperationsToolbar
            selectedPosts={selectedPosts}
            categories={categories}
            tags={tags}
            onComplete={handleBulkOperationComplete}
          />
        }
      />

      {/* Confirm Dialog */}
      <ConfirmDialog />

      <HeadlineReviewDialog
        post={headlineReviewPost}
        open={!!headlineReviewPost}
        onClose={() => setHeadlineReviewPost(null)}
      />

      <OutlineReviewDialog
        post={outlineReviewPost}
        open={!!outlineReviewPost}
        onClose={() => setOutlineReviewPost(null)}
      />

      <LengthWarningDialog
        post={lengthWarningPost}
        open={!!lengthWarningPost}
        onClose={() => setLengthWarningPost(null)}
      />
    </div>
  )
}